import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useGetOrganizationsQuery } from '../../api/organizationApi';
import { useCreateGroupMutation } from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import useAutoSelectFirst from '../../hooks/useAutoSelectFirst';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import { TextEditor } from '../../components/TextEditor/TextEditor';
import BroadcastToggleField from '../../components/BroadcastToggleField/BroadcastToggleField';
import ChannelsField from '../../components/ChannelsField/ChannelsField';
import TileImageField from '../../components/TileImageField/TileImageField';
import buttons from '../../styles/shared/buttons.module.css';

export default function GroupCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();

  const { data: organizations, isLoading: loadingOrgs } = useGetOrganizationsQuery();
  const [createGroup, { isLoading: isCreating }] = useCreateGroupMutation();
  const [tileFile, setTileFile] = useState(null);

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      organizationId: location.state?.organizationId ?? '',
      name: '',
      terms: '',
      channels: ['email'],
      broadcast: true,
    },
  });
  const organizationId = watch('organizationId');
  const broadcast = watch('broadcast');
  const name = watch('name');

  useAutoSelectFirst(organizations, organizationId, (id) => setValue('organizationId', id));

  const onCreate = async (data) => {
    try {
      await createGroup({
        organizationId: data.organizationId,
        name: data.name.trim(),
        tileFile,
        terms: data.terms.trim() || undefined,
        channels: data.channels,
        broadcast: data.broadcast,
      }).unwrap();
      showToast('success', 'Groups', 'Group created.');
      navigate('/admin/groups');
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not create group.'));
    }
  };

  if (loadingOrgs) return <p className="textMuted">Loading…</p>;

  return (
    <EntityFormPage title="Add group" backTo="/admin/groups">
      <form onSubmit={handleSubmit(onCreate)}>
        <Controller
          name="organizationId"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Organization</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={organizations?.map((c) => ({ label: `${c.name} (${c.slug})`, value: c.id }))}
                className="w-full mb-3"
              />
            </div>
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Name</label>
              <InputText id={field.name} className="w-full mb-3" placeholder="Pizza Station" {...field} />
            </div>
          )}
        />

        <TileImageField
          file={tileFile}
          onFileChange={setTileFile}
          name={name}
          onError={(msg) => showToast('error', 'Groups', msg)}
        />

        <Controller
          name="terms"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Terms &amp; conditions</label>
              <TextEditor value={field.value} onTextChange={(e) => field.onChange(e.htmlValue ?? '')} />
            </div>
          )}
        />

        <ChannelsField control={control} broadcast={broadcast} />

        <BroadcastToggleField control={control} setValue={setValue} />

        <p className="textMuted mb-3">
          Groups start <strong>disabled</strong>.
        </p>

        <Button type="submit" label="Save" className={buttons.saveBtn} loading={isCreating} />
      </form>
    </EntityFormPage>
  );
}
